import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Menu, Search, ShoppingCart, User, X } from 'lucide-react';
import { MegaMenu, useCart, useClickOutside } from '@kss/ui';
import { publicCatalogService, StorefrontMenuKind, type StorefrontMenu } from '@kss/api';
import { useAuth, isPanelRole } from '@kss/auth';
import { CustomerAccountDropdown } from './CustomerAccountDropdown';

/**
 * Dispatched when an unauthenticated user clicks "Log in" or "Open Panel".
 * The web app root listens for this event and opens the login modal.
 *
 * `detail.redirect` (optional) — where to navigate after a successful sign-in.
 */
function requestLogin(redirect?: string) {
  window.dispatchEvent(
    new CustomEvent('kss:open-login', { detail: { redirect } })
  );
}

function requestRegister(redirect?: string) {
  window.dispatchEvent(
    new CustomEvent('kss:open-register', { detail: { redirect } })
  );
}

interface NavChild {
  to: string;
  label: string;
  description?: string;
  /**
   * Small pill rendered beside the label, e.g. "Hot", "New", "Save 30%".
   * The hardcoded mega menus always passed this through to `MegaMenu`; it was
   * simply never declared on the interface.
   */
  badge?: string;
  /**
   * Action key — when set, the click runs custom logic instead of
   * navigating. `open-login` opens the sign-in modal; `open-register`
   * opens the create-account modal.
   */
  action?: 'open-login' | 'open-register';
  /** Optional redirect to forward to the modal (`detail.redirect`). */
  loginRedirect?: string;
}
interface NavGroup {
  id: string;
  label: string;
  to?: string;
  children?: NavChild[];
}

const navGroups: NavGroup[] = [
  { id: 'home', label: 'Home', to: '/' },
  {
    id: 'company',
    label: 'Company',
    children: [
      { to: '/about', label: 'About', description: 'Our story' },
      { to: '/contact', label: 'Contact', description: 'Get in touch' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    children: [
      { to: '#login', label: 'Log in', description: 'Sign into KSS', action: 'open-login' },
      { to: '#register', label: 'Sign up', description: 'Create a new account', action: 'open-register' },
      {
        to: '#open-panel',
        label: 'Open Panel',
        description: 'Go to your workspace',
        action: 'open-login',
        loginRedirect: '/panel/dashboard',
      },
    ],
  },
];

function Dropdown({ group }: { group: NavGroup }) {
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-content-secondary hover:text-content-primary"
      >
        {group.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && group.children && (
        <div className="absolute left-0 top-full z-50 min-w-[220px] rounded-md border border-border bg-surface-card py-2 shadow-xl">
          {group.children.map((c) => {
            // For auth-action items, suppress navigation and dispatch the
            // matching global modal event. Already-authenticated users on an
            // "Open Panel" item bypass the modal and go straight to the panel.
            if (c.action === 'open-login' || c.action === 'open-register') {
              if (isAuthenticated && c.loginRedirect) {
                return (
                  <Link
                    key={c.to}
                    to={c.loginRedirect}
                    onClick={() => setOpen(false)}
                    className="flex flex-col gap-0.5 px-4 py-2 text-sm transition-colors hover:bg-surface-card-hover"
                  >
                    <span className="font-medium text-content-primary">{c.label}</span>
                    {c.description && (
                      <span className="text-xs text-content-muted">{c.description}</span>
                    )}
                  </Link>
                );
              }
              if (isAuthenticated && !c.loginRedirect) {
                // Already signed-in users don't need a "Log in" / "Sign up" entry.
                return null;
              }
              return (
                <button
                  key={c.to}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (c.action === 'open-register') {
                      requestRegister(c.loginRedirect);
                    } else {
                      requestLogin(c.loginRedirect);
                    }
                  }}
                  className="flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left text-sm transition-colors hover:bg-surface-card-hover"
                >
                  <span className="font-medium text-content-primary">{c.label}</span>
                  {c.description && (
                    <span className="text-xs text-content-muted">{c.description}</span>
                  )}
                </button>
              );
            }
            return (
              <Link
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                className="flex flex-col gap-0.5 px-4 py-2 text-sm transition-colors hover:bg-surface-card-hover"
              >
                <span className="font-medium text-content-primary">{c.label}</span>
                {c.description && (
                  <span className="text-xs text-content-muted">{c.description}</span>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Curated fallback for the Shop mega menu — only used when
   `/api/Public/categories` returns an empty list (e.g. brand-new
   tenant). Trimmed down on purpose: no more "Electronics" or other
   hardcoded categories that the panel admin can't manage. Once the
   tenant has any active category, `useDynamicMegaMenus` swaps in the
   live list. Bug ref: AI Agent — "Menu and category updates are not
   reflecting correctly; remove unwanted categories like Electronics
   and other unnecessary multiple categories". */
const shopMega: MegaShape = {
  label: 'Shop',
  columns: [
    {
      title: 'Browse',
      links: [
        { to: '/shop', label: 'All products', description: 'Full catalog' },
        { to: '/shop?sort=new', label: 'New arrivals', description: 'Latest additions' },
        { to: '/shop?sort=pop', label: 'Best sellers', description: 'Most popular' },
      ],
    },
    {
      /* Only the one link that is true regardless of catalog contents.
         The price-bucket and "lightning deals" tiles that used to sit
         here all pointed at the same `?sale=true` URL, so three
         differently-labelled links produced one identical result set.
         Real buckets are derived from live prices in
         `useDynamicMegaMenus`. */
      title: 'Deals',
      links: [{ to: '/shop?sale=true', label: 'Today\'s deals', badge: 'Hot' }],
    },
  ],
  featured: {
    title: 'Shop the sale',
    description: 'Discounted picks across the catalog',
    to: '/shop?sale=true',
    cta: 'Shop now',
  },
};

const brandsMega: MegaShape = {
  /* Brands fallback — only used when `/api/Public/brands` returns
     empty. Trimmed to a single "Browse" column with a couple of
     generic shortcuts; specific brand names are now driven entirely
     by the live API via `useDynamicMegaMenus`. */
  label: 'Brands',
  columns: [
    {
      title: 'Browse',
      links: [
        { to: '/shop', label: 'All brands', description: 'Browse the full list' },
        { to: '/shop?sort=pop', label: 'Top sellers' },
        { to: '/shop?sort=new', label: 'New brands' },
      ],
    },
  ],
};

/**
 * Last-resort Deals menu — a brand-new tenant with no discounted stock.
 *
 * Every link here resolves to a filter `ShopList` actually implements.
 * The previous version advertised `?d=lightning`, `?d=under-499`,
 * `?d=clearance`, `?d=bundles` and `?d=new`; `ShopList` has never read a
 * `d` param, so all six tiles dumped the shopper on the unfiltered
 * catalog. Invented commercial claims ("Save 30%", "Bundle savings")
 * went with them — no tenant had configured either.
 */
const dealsMega: MegaShape = {
  label: 'Deals',
  columns: [
    {
      title: 'Today',
      links: [
        { to: '/shop?sale=true', label: 'All deals', badge: 'Hot' },
        { to: '/shop?sort=new', label: 'New arrivals', badge: 'New' },
      ],
    },
  ],
};

/** Price ceilings offered as deal tiles, smallest first. */
const DEAL_PRICE_BUCKETS = [499, 1000, 2000, 5000];

/** ₹1,234 — matches the storefront's price formatting. */
function formatInr(n: number): string {
  return `₹${n.toLocaleString('en-IN')}`;
}

/**
 * Build the Deals menu from products that carry a real discount
 * (`mrpPrice > sellingPrice`, surfaced by the public catalog as
 * `originalPrice`).
 *
 * Returns `null` when the tenant has no discounted stock, so the caller
 * can fall through to `dealsMega` rather than render a menu of tiles
 * that all resolve to zero products.
 *
 * Two derived columns:
 *   "By price"    — only buckets that actually contain discounted rows,
 *                   so "Under ₹499" never appears when the cheapest
 *                   discounted item is ₹800.
 *   "By category" — the categories holding the most discounted rows.
 *
 * Discount percentages in the badges are computed from the live rows,
 * not authored, so they can't overstate the offer.
 */
function buildDealsMenu(
  rows: ReadonlyArray<{ price: number; originalPrice?: number; category?: string }>,
): MegaShape | null {
  const discounted = rows.filter(
    (r) => r.originalPrice != null && r.originalPrice > r.price && r.price > 0,
  );
  if (discounted.length === 0) return null;

  const pct = (r: { price: number; originalPrice?: number }) =>
    Math.round(((r.originalPrice! - r.price) / r.originalPrice!) * 100);
  const topPct = Math.max(...discounted.map(pct));

  const priceLinks: NavChild[] = [
    {
      to: '/shop?sale=true',
      label: 'All deals',
      description: `${discounted.length} discounted item${discounted.length === 1 ? '' : 's'}`,
      badge: topPct > 0 ? `Up to ${topPct}% off` : undefined,
    },
  ];
  for (const cap of DEAL_PRICE_BUCKETS) {
    const n = discounted.filter((r) => r.price <= cap).length;
    // Skip an empty bucket, and skip one that repeats the previous
    // bucket's result set — "Under ₹1,000" adding nothing over
    // "Under ₹499" is noise, not a choice.
    if (n === 0) continue;
    const prev = priceLinks[priceLinks.length - 1]?.description;
    const desc = `${n} item${n === 1 ? '' : 's'}`;
    if (desc === prev) continue;
    priceLinks.push({
      to: `/shop?sale=true&maxPrice=${cap}`,
      label: `Under ${formatInr(cap)}`,
      description: desc,
    });
  }

  const byCategory = new Map<string, number>();
  for (const r of discounted) {
    const c = (r.category ?? '').trim();
    if (c) byCategory.set(c, (byCategory.get(c) ?? 0) + 1);
  }
  const categoryLinks: NavChild[] = [...byCategory.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, n]) => ({
      to: `/shop?sale=true&c=${encodeURIComponent(name.toLowerCase())}`,
      label: name,
      description: `${n} deal${n === 1 ? '' : 's'}`,
    }));

  const columns = [{ title: 'By price', links: priceLinks }];
  if (categoryLinks.length > 0) {
    columns.push({ title: 'By category', links: categoryLinks });
  }
  return { label: 'Deals', columns };
}

/**
 * Build the Shop mega-menu dynamically from `/api/Public/categories`
 * and `/api/Public/brands`. Falls back to the curated marketing list
 * above only when the API returns nothing (empty tenant) — the
 * hardcoded list stops the menu looking broken on a fresh deployment.
 *
 * Bug ref: AI Agent (round 2) — "Front Shop menu, category, and
 * brand list are static and should be fetched dynamically from API
 * as per master data."
 */
/** Promo tile rendered to the right of a mega menu's columns. */
interface MegaFeatured {
  title: string;
  description: string;
  to: string;
  cta: string;
}

/**
 * A mega menu in the shape this header renders.
 *
 * The built-in fallbacks below are annotated with this type rather than
 * left to inference: inferred literal types made their `links` arrays
 * `{to, label, description}[]` (no optional `badge`), so a derived
 * column of `NavChild[]` couldn't be substituted into one.
 */
type MegaShape = {
  label: string;
  columns: { title: string; links: NavChild[] }[];
  featured?: MegaFeatured;
};

/**
 * Convert one admin-managed `StorefrontMenu` node into the header's mega shape.
 *
 * A Menu with Column children becomes a multi-column mega menu. A Menu whose
 * children are Links (no columns) is folded into a single unnamed column, so an
 * admin doesn't have to create a column just to add two links.
 */
function toMega(node: StorefrontMenu): MegaShape {
  const kids = (node.children ?? []).filter((c) => c.isActive !== false);
  const asLink = (l: StorefrontMenu): NavChild => ({
    to: l.url || '#',
    label: l.label ?? '',
    description: l.description || undefined,
    badge: l.badge || undefined,
  });

  const columns = kids.some((c) => c.kind === StorefrontMenuKind.Column)
    ? kids
        .filter((c) => c.kind === StorefrontMenuKind.Column)
        .map((c) => ({
          title: c.label ?? '',
          links: (c.children ?? []).filter((l) => l.isActive !== false).map(asLink),
        }))
        .filter((c) => c.links.length > 0)
    : [{ title: '', links: kids.map(asLink) }].filter((c) => c.links.length > 0);

  return { label: node.label ?? '', columns };
}

function useDynamicMegaMenus() {
  const [dynamicShop, setDynamicShop] = useState<MegaShape | null>(null);
  const [dynamicBrands, setDynamicBrands] = useState<MegaShape | null>(null);
  /** Deals derived from live discounted stock — see `buildDealsMenu`. */
  const [dynamicDeals, setDynamicDeals] = useState<MegaShape | null>(null);
  /**
   * Admin-configured menus keyed by lowercased label. These OUTRANK both the
   * category-derived menus and the hardcoded fallbacks: if an admin has
   * explicitly configured "Deals", that is what the storefront shows.
   */
  const [adminMenus, setAdminMenus] = useState<Record<string, MegaShape>>({});
  /** Admin menus that aren't one of the three built-ins — rendered as extras. */
  const [adminExtras, setAdminExtras] = useState<MegaShape[]>([]);
  /** Admin top-level entries that are plain links (a url, no children). */
  const [adminLinks, setAdminLinks] = useState<NavChild[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const nav = await publicCatalogService.getNav('header').catch(() => []);
        if (cancelled || !nav || nav.length === 0) return;

        const active = nav.filter((n) => n.isActive !== false);
        const byLabel: Record<string, MegaShape> = {};
        const extras: MegaShape[] = [];
        const links: NavChild[] = [];
        const BUILT_IN = new Set(['shop', 'brands', 'deals']);

        for (const node of active) {
          const kids = (node.children ?? []).filter((c) => c.isActive !== false);
          const key = (node.label ?? '').trim().toLowerCase();

          // A top-level entry with a url and no children is a plain bar link.
          if (kids.length === 0) {
            if (node.url) {
              links.push({
                to: node.url,
                label: node.label ?? '',
                description: node.description || undefined,
                badge: node.badge || undefined,
              });
            }
            continue;
          }

          const mega = toMega(node);
          if (mega.columns.length === 0) continue;
          if (BUILT_IN.has(key)) byLabel[key] = mega;
          else extras.push(mega);
        }

        setAdminMenus(byLabel);
        setAdminExtras(extras);
        setAdminLinks(links);
      } catch {
        // Silent — the built-in menus below remain in place.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cats, brands, prods] = await Promise.all([
          publicCatalogService.categories().catch(() => []),
          publicCatalogService.brands().catch(() => []),
          /* Full set: the deal buckets count discounted rows across the
             whole catalog, so a single page would understate them. */
          publicCatalogService.allProducts().catch(() => []),
        ]);
        if (cancelled) return;

        /* Deals from live prices. `/Public/products` returns the raw
           product shape, so map to the discount fields directly rather
           than routing through the storefront adapter (which lives in
           apps/web and isn't importable from this package). */
        const catNameById = new Map<string, string>();
        for (const c of cats ?? []) {
          if (c.categoryId && c.name) catNameById.set(c.categoryId, c.name);
        }
        const dealRows = (prods ?? [])
          .filter((p) => p.isActive !== false)
          .map((p) => {
            const price = Number(p.sellingPrice ?? 0);
            const mrp = Number(p.mrpPrice ?? 0);
            return {
              price,
              originalPrice: mrp > price ? mrp : undefined,
              category: (p.categoryId && catNameById.get(p.categoryId)) || undefined,
            };
          });
        const derivedDeals = buildDealsMenu(dealRows);
        setDynamicDeals(derivedDeals);

        const catLinks = (cats ?? [])
          .filter((c) => c.isActive !== false && c.name)
          .slice(0, 8)
          .map((c) => ({
            to: `/shop?c=${encodeURIComponent(c.name!.toLowerCase())}`,
            label: c.name!,
            description: c.description ?? undefined,
          }));
        const brandLinks = (brands ?? [])
          .filter((b) => b.isActive !== false && b.name)
          .map((b) => b.name!)
          .sort((a, b) => a.localeCompare(b));

        if (catLinks.length > 0) {
          setDynamicShop({
            ...shopMega,
            columns: [
              { title: 'Categories', links: catLinks },
              // Featured brands column — first 4 brands from the live list.
              {
                title: 'Featured brands',
                links: brandLinks.slice(0, 4).map((n) => ({
                  to: `/shop?b=${encodeURIComponent(n.toLowerCase())}`,
                  label: n,
                  description: 'Brand',
                })),
              },
              /* Deals tile alongside the live categories + brands. Prefer
                 the price buckets derived from actual discounted stock;
                 fall back to the single generic "Today's deals" link when
                 the tenant has none. Indices on `shopMega`:
                 0 = Browse, 1 = Deals. */
              derivedDeals?.columns[0] ?? shopMega.columns[1],
            ],
          });
        }
        if (brandLinks.length > 0) {
          // Chunk brand list into 3 columns of up to 6 each.
          const chunk = Math.ceil(brandLinks.length / 3);
          const cols = [0, 1, 2].map((i) => ({
            title: i === 0 ? 'A–H' : i === 1 ? 'I–P' : 'Q–Z',
            links: brandLinks.slice(i * chunk, (i + 1) * chunk).map((n) => ({
              to: `/shop?b=${encodeURIComponent(n.toLowerCase())}`,
              label: n,
              description: 'Brand', // Generic — keeps the type homogeneous with brandsMega.
            })),
          })).filter((c) => c.links.length > 0);
          if (cols.length > 0) setDynamicBrands({ label: 'Brands', columns: cols });
        }
      } catch {
        // Silent — fall back to the marketing-curated lists.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Precedence, highest first:
       1. Admin-configured StorefrontMenu — an explicit editorial decision.
       2. Live categories / brands — derived from master data.
       3. Hardcoded fallback — brand-new tenant with neither.
     Deals now follows the same three tiers as Shop and Brands: an admin's
     explicit menu, else buckets derived from live discounted stock, else
     the minimal fallback. */
  return {
    shopMenu: adminMenus.shop ?? dynamicShop ?? shopMega,
    brandsMenu: adminMenus.brands ?? dynamicBrands ?? brandsMega,
    dealsMenu: adminMenus.deals ?? dynamicDeals ?? dealsMega,
    extraMenus: adminExtras,
    extraLinks: adminLinks,
  };
}

export function WebHeader() {
  const navigate = useNavigate();
  const cart = useCart();
  const { isAuthenticated, user } = useAuth();
  const { shopMenu, brandsMenu, dealsMenu, extraMenus, extraLinks } = useDynamicMegaMenus();
  // Any non-customer role lands on the admin panel; customers land on
  // the public account dashboard. Uses the shared `isPanelRole` denylist
  // so new backend roles (SuperAdmin / TenantAdmin / Owner / …) route
  // correctly without code changes here.
  const accountTarget = isPanelRole(user?.role) ? '/panel/dashboard' : '/account';
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  /* Auto-close the mobile menu + the search box on any route change.
     Without this, a user who navigates via a non-drawer link (back
     button, etc.) ends up looking at the drawer overlaying the new
     page. */
  const location = useLocation();
  useEffect(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
    setSearchOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const closeSearch = () => {
    setSearchOpen(false);
    setQ('');
  };

  /**
   * Search submit. Two-tier behaviour:
   *
   *   1. Single-word navigation shortcuts ("cart", "about", "panel", …)
   *      jump straight to that page — useful for power-users who treat
   *      the box like an omnibox.
   *   2. Anything else is a real product search: forward the query as
   *      `?q=<term>` on `/shop`. `ShopList` reads it via
   *      `useSearchParams` and filters the catalog by title / brand /
   *      description match.
   *
   * The query is URL-encoded (handled by `URLSearchParams`) so spaces
   * + special chars don't break the link.
   */
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = q.trim();
    if (!raw) return;
    const lower = raw.toLowerCase();
    // Bare-keyword shortcuts: only triggered when the input is JUST
    // the keyword (no spaces) so a search for "about my new sofa"
    // doesn't get hijacked.
    if (!lower.includes(' ')) {
      if (lower === 'component' || lower === 'components') return navigate('/panel/components');
      if (lower === 'cart') return navigate('/cart');
      if (lower === 'demo') return navigate('/demo');
      if (lower === 'about') return navigate('/about');
      if (lower === 'contact') return navigate('/contact');
      if (lower === 'panel' || lower === 'dashboard') return navigate('/panel');
    }
    // Real product search.
    const params = new URLSearchParams({ q: raw });
    navigate(`/shop?${params.toString()}`);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-surface-topbar shadow-sm">
      {/* Gutters unified 2026-05-20 — header / shop / sidebar-grid all
          share a 16px horizontal margin (`px-4`) so the logo, slider
          edges (full-bleed below), and the categories-left / products-
          right split align consistently across breakpoints. The
          previous 1px gutter + `max-w-7xl` left big empty bands on
          wide monitors. `relative` lets the search form (when open)
          overlay the nav strip absolutely. */}
      <div className="relative flex h-16 w-full items-center gap-2 px-4 sm:gap-4">
        {/* Mobile hamburger — LEFT side on phones, hidden on md+.
            Toggles between Menu / X icons so the button doubles as
            the close affordance. Per user request 2026-05-12. */}
        <button
          type="button"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((o) => !o)}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border text-content-secondary hover:bg-surface-card-hover hover:text-content-primary md:hidden"
        >
          {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>

        {/* Logo — left-aligned on md+, centered on phones (the
            hamburger and the right-side icons together pin it to the
            middle via `mx-auto md:mx-0`). */}
        <Link
          to="/"
          className="mx-auto flex shrink-0 items-center gap-2 text-lg font-bold text-content-primary md:mx-0 md:mr-auto"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            K
          </span>
          <span className="flex flex-col leading-tight">
            <span>KSS</span>
            <span className="hidden text-[10px] font-medium text-content-muted sm:inline">
              Kristy Mart
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navGroups.map((g) =>
            g.children ? (
              <Dropdown key={g.id} group={g} />
            ) : (
              <Link
                key={g.id}
                to={g.to!}
                className="rounded-md px-3 py-2 text-sm font-medium text-content-secondary hover:text-content-primary"
              >
                {g.label}
              </Link>
            )
          )}
          <MegaMenu
            label="Shop"
            columns={shopMenu.columns}
            featured={shopMenu.featured}
          />
          <MegaMenu label="Brands" columns={brandsMenu.columns} width={640} />
          <MegaMenu label="Deals" columns={dealsMenu.columns} width={520} />
          {/* Menus the admin added beyond the three built-ins, plus any
              top-level entries configured as plain links. */}
          {extraMenus.map((m) => (
            <MegaMenu key={m.label} label={m.label} columns={m.columns} width={520} />
          ))}
          {extraLinks.map((l) => (
            <Link
              key={`${l.to}-${l.label}`}
              to={l.to}
              className="rounded-md px-3 py-2 text-sm font-medium text-content-secondary hover:text-content-primary"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* (Hamburger moved to the LEFT of the header — see above.) */}

        {/* Search form — absolutely positioned when open so it
            OVERLAYS the nav strip instead of squeezing it. The form
            stretches from the logo's right edge to the icon cluster
            with a small gutter on each side; covers the Home / Shop
            / Brands / Deals / Company / Account row entirely. */}
        {searchOpen ? (
          <form
            onSubmit={(e) => { handleSearch(e); closeSearch(); }}
            className="absolute inset-y-0 left-16 right-24 z-20 flex items-center gap-2 rounded-md border border-primary bg-surface-card px-3 py-1.5 my-2 shadow-sm sm:left-44 sm:right-32 md:left-48 md:right-40"
          >
            <Search className="h-4 w-4 shrink-0 text-content-muted" />
            <input
              ref={searchInputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
              placeholder="Search products, pages..."
              className="min-w-0 flex-1 bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted"
            />
            <button
              type="button"
              aria-label="Close search"
              onClick={closeSearch}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-content-muted hover:bg-surface-card-hover hover:text-content-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </form>
        ) : null}

        <div className="flex shrink-0 items-center gap-2">
          {!searchOpen && (
            <button
              type="button"
              aria-label="Open search"
              onClick={() => setSearchOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-content-secondary hover:bg-surface-card-hover hover:text-content-primary"
            >
              <Search className="h-4 w-4" />
            </button>
          )}

          {/* Cart icon is ALWAYS visible — guests can build a cart
              and we redirect to login at checkout instead. Per Fix 8:
              "Cart icon present on every page; badge reactive". */}
          <button
            type="button"
            onClick={() => cart.openDrawer()}
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-content-secondary hover:bg-surface-card-hover hover:text-content-primary"
            aria-label="Cart"
          >
            <ShoppingCart className="h-4 w-4" />
            {cart.count > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
                {cart.count}
              </span>
            )}
          </button>

          {/* Sign-in icon (unauth) or avatar link (auth). The big "Log in"
              pill is gone per request, but a small icon button keeps the
              auth flow discoverable from the menu bar without the visual
              weight of a labelled button. */}
          {/* Authenticated customers get a full dropdown (Orders /
              Track order / Profile / Settings / Addresses / Wishlist
              / Notifications / Sign out). Panel-role users still get
              a simple "Open panel" shortcut. Bug ref: AI Agent
              2026-05-14 — "login button dropdown should show order,
              profile, setting, track order". */}
          {isAuthenticated ? (
            isPanelRole(user?.role) ? (
              <Link
                to="/panel/dashboard"
                aria-label="Open panel"
                title="Open panel"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary-hover"
              >
                <User className="h-4 w-4" />
              </Link>
            ) : (
              <CustomerAccountDropdown />
            )
          ) : (
            <button
              type="button"
              onClick={() => requestLogin()}
              aria-label="Sign in"
              title="Sign in"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary-hover"
            >
              <User className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu — slide-in drawer from the LEFT (filters-style)
          on small screens. Per user request 2026-05-12: "menu should
          be show as filters type when mobile view". Same backdrop +
          slide animation pattern the shop filter rail uses. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 md:hidden"
          onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
          aria-hidden="true"
        />
      )}
      {/* Drawer is conditionally MOUNTED rather than always-mounted
          with a transition: an earlier `transition-transform` /
          `transition-[left]` toggle hit a stubborn Chrome computed-
          style edge case where the off-screen state was sticky
          regardless of inline style. Conditional mount sidesteps
          the issue and the user experience is identical. */}
      {mobileOpen && (
      <aside
        className="fixed left-0 top-0 z-[51] flex h-full w-80 max-w-[85vw] flex-col overflow-y-auto border-r border-border bg-surface-card md:hidden"
        aria-label="Site navigation"
      >
        {/* Drawer header — KSS mark + close X. Matches the shop
            filter drawer's header pattern. */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-surface-card px-4 py-3">
          <Link
            to="/"
            onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
            className="flex items-center gap-2 text-sm font-bold text-content-primary"
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              K
            </span>
            KSS · Kristy Mart
          </Link>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-content-secondary hover:bg-surface-card-hover hover:text-content-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {mobileOpen && (
          <nav className="grid divide-y divide-border">
            <MobileGroup
              label="Shop"
              expanded={mobileExpanded === 'shop'}
              onToggle={() => setMobileExpanded((p) => (p === 'shop' ? null : 'shop'))}
              columns={shopMenu.columns}
              onLinkClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
            />
            <MobileGroup
              label="Brands"
              expanded={mobileExpanded === 'brands'}
              onToggle={() => setMobileExpanded((p) => (p === 'brands' ? null : 'brands'))}
              columns={brandsMenu.columns}
              onLinkClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
            />
            <MobileGroup
              label="Deals"
              expanded={mobileExpanded === 'deals'}
              onToggle={() => setMobileExpanded((p) => (p === 'deals' ? null : 'deals'))}
              columns={dealsMenu.columns}
              onLinkClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
            />
            {/* Admin-added menus + plain links, mirroring the desktop bar. */}
            {extraMenus.map((m) => (
              <MobileGroup
                key={m.label}
                label={m.label}
                expanded={mobileExpanded === `x-${m.label}`}
                onToggle={() =>
                  setMobileExpanded((p) => (p === `x-${m.label}` ? null : `x-${m.label}`))
                }
                columns={m.columns}
                onLinkClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
              />
            ))}
            {extraLinks.map((l) => (
              <Link
                key={`m-${l.to}-${l.label}`}
                to={l.to}
                onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
                className="block px-4 py-2.5 text-sm font-medium text-content-secondary"
              >
                {l.label}
              </Link>
            ))}
            {navGroups.map((g) =>
              g.children ? (
                <MobileGroup
                  key={g.id}
                  label={g.label}
                  expanded={mobileExpanded === g.id}
                  onToggle={() => setMobileExpanded((p) => (p === g.id ? null : g.id))}
                  columns={[{ title: g.label, links: g.children.map((c) => ({ to: c.to, label: c.label, description: c.description })) }]}
                  onLinkClick={(child) => {
                    setMobileOpen(false);
                    setMobileExpanded(null);
                    const original = g.children?.find((c) => c.to === child.to);
                    if (original?.action === 'open-register') {
                      requestRegister(original.loginRedirect);
                    } else if (original?.action === 'open-login') {
                      requestLogin(original.loginRedirect);
                    }
                  }}
                />
              ) : (
                <Link
                  key={g.id}
                  to={g.to!}
                  onClick={() => { setMobileOpen(false); setMobileExpanded(null); }}
                  className="block px-4 py-3 text-sm font-medium text-content-primary hover:bg-surface-card-hover"
                >
                  {g.label}
                </Link>
              )
            )}
          </nav>
        )}
      </aside>
      )}
    </header>
  );
}

interface MobileGroupProps {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  columns: { title: string; links: { to: string; label: string; description?: string; badge?: string }[] }[];
  onLinkClick: (link: { to: string; label: string }) => void;
}

function MobileGroup({ label, expanded, onToggle, columns, onLinkClick }: MobileGroupProps) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-content-primary hover:bg-surface-card-hover"
        aria-expanded={expanded}
      >
        {label}
        <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="bg-surface-app/40 px-2 py-2">
          {columns.map((col) => (
            <div key={col.title} className="px-2 py-1">
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-content-muted">
                {col.title}
              </p>
              {col.links.map((l) => (
                <Link
                  key={l.to + l.label}
                  to={l.to}
                  onClick={() => onLinkClick(l)}
                  className="flex items-center justify-between rounded px-2 py-2 text-sm text-content-primary hover:bg-surface-card-hover"
                >
                  <span>
                    <span className="font-medium">{l.label}</span>
                    {l.description && (
                      <span className="ml-2 text-xs text-content-muted">{l.description}</span>
                    )}
                  </span>
                  {l.badge && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                      {l.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WebHeader;
